import { useState, useEffect, useCallback } from 'react';
import { Venue, VenueSearchFilters, VenueSearchResponse } from '../../types/venue';
import { apiRequest } from '../../utils/apiUtils';

interface UsePagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface UseVenuesReturn {
  venues: Venue[];
  filters: VenueSearchFilters;
  pagination: UsePagination;
  loading: boolean;
  error: string | null;
  searchVenues: () => Promise<void>;
  updateFilters: (newFilters: Partial<VenueSearchFilters>) => void;
  resetFilters: () => void;
  goToPage: (page: number) => void;
}

const DEFAULT_FILTERS: VenueSearchFilters = {
  page: 1,
  limit: 20,
  sortBy: 'name',
  sortOrder: 'asc',
};

export const useVenues = (initialFilters?: Partial<VenueSearchFilters>): UseVenuesReturn => {
  const [venues, setVenues] = useState<Venue[]>([]);
  const [filters, setFilters] = useState<VenueSearchFilters>({
    ...DEFAULT_FILTERS,
    ...initialFilters,
  });
  const [pagination, setPagination] = useState<UsePagination>({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const searchVenues = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Build query params
      const params = new URLSearchParams();

      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });

      const response = await apiRequest<VenueSearchResponse>(
        `/venues?${params.toString()}`,
        { requireAuth: false }
      );

      if (response.data) {
        setVenues(response.data.venues);
        setPagination(response.data.pagination);
      } else {
        setError(response.error || 'Error al buscar canchas');
        setVenues([]);
      }
    } catch (err) {
      setError('Error de conexión al buscar canchas');
      setVenues([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const updateFilters = useCallback((newFilters: Partial<VenueSearchFilters>) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
      page: 1, // Reset to page 1 when filters change
    }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
  }, []);

  const goToPage = useCallback((page: number) => {
    setFilters((prev) => ({
      ...prev,
      page,
    }));
  }, []);

  // Trigger search when filters change
  useEffect(() => {
    searchVenues();
  }, [searchVenues]);

  return {
    venues,
    filters,
    pagination,
    loading,
    error,
    searchVenues,
    updateFilters,
    resetFilters,
    goToPage,
  };
};
